import { create } from 'zustand';

/**
 * A newer Android app release, from the server's update feed (/android/latest.json, published with
 * apps/mobile/scripts/publish.mjs). The app is sideloaded, so this feed is its only update channel.
 */
export interface AppRelease {
  /** Android versionCode: major*10000 + minor*100 + patch. */
  versionCode: number;
  versionName: string;
  /** Absolute https URL of the APK (the feed may give it relative to itself). */
  url: string;
  /** What's new, plain text; line breaks are kept. */
  notes?: string;
  /** Hides "Later": this version is needed to keep using Spawnwire. */
  required?: boolean;
  /** Hex SHA-256 of the APK; the download is checked against it. */
  sha256?: string;
  /** APK size in bytes, shown before downloading. */
  size?: number;
}

/** The update screen, as the Android app shows it. Unused on the website. */
export interface AppUpdateState {
  /** The release on offer; null hides the screen. */
  release: AppRelease | null;
  /** The installed version name, shown for comparison. */
  installed: string | null;
  phase: 'idle' | 'downloading' | 'installing' | 'error';
  /** Download progress from 0 to 1, or null while the size is unknown. */
  progress: number | null;
  error: string | null;
}

export const useAppUpdate = create<AppUpdateState>(() => ({
  release: null,
  installed: null,
  phase: 'idle',
  progress: null,
  error: null,
}));

/** Checks a feed's shape; anything unexpected means no offer rather than a broken screen. */
export function parseRelease(data: unknown, feedUrl: string): AppRelease | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (typeof d.versionCode !== 'number' || !Number.isInteger(d.versionCode) || d.versionCode <= 0) return null;
  if (typeof d.versionName !== 'string' || !d.versionName.trim() || typeof d.url !== 'string') return null;
  let url: URL;
  try {
    url = new URL(d.url, feedUrl);
  } catch {
    return null;
  }
  // Plain http only for a local test server; the phone itself refuses cleartext anyway.
  if (url.protocol !== 'https:' && !(url.protocol === 'http:' && new URL(feedUrl).protocol === 'http:')) return null;
  const release: AppRelease = { versionCode: d.versionCode, versionName: d.versionName.trim().slice(0, 40), url: url.href };
  if (typeof d.notes === 'string' && d.notes.trim()) release.notes = d.notes.trim().slice(0, 4000);
  if (d.required === true) release.required = true;
  if (typeof d.sha256 === 'string' && /^[0-9a-f]{64}$/i.test(d.sha256)) release.sha256 = d.sha256.toLowerCase();
  if (typeof d.size === 'number' && d.size > 0) release.size = d.size;
  return release;
}

/** Whether to show the update screen: the release is newer, and not put off with "Later" unless it is required. */
export function shouldOffer(release: AppRelease | null, installedCode: number, laterCode: number | null): release is AppRelease {
  if (!release || release.versionCode <= installedCode) return false;
  return !!release.required || laterCode !== release.versionCode;
}
